import { useState } from "react";
import { Plus, Check, X, MoreHorizontal } from "lucide-react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  StatusPill,
  MetaPill,
  Skeleton,
  Input,
  Alert,
  ConfirmDialog,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { PackDialog, type PackForEdit } from "./pack-dialog.tsx";
import { ScaleDialog } from "./scale-dialog.tsx";

const NEEDS_MANAGE = "Needs catalog manage permission";

/**
 * Store admin — Packs library (spec 13 / design 14, S-PK1).
 *
 * One page, two sections: the store's size scales, then its pack library.
 * Every product price is per pack (spec 13). Referenced packs offer "New
 * version", never in-place Edit (R3).
 */
export function PacksListPage() {
  const canManage = usePermission("store.catalog.manage");
  const utils = trpc.useUtils();

  const scales = trpc.storePacks.listScales.useQuery();
  const packs = trpc.storePacks.list.useQuery();

  const [packDialog, setPackDialog] = useState<{ mode: "create" | "version" | "edit"; pack?: PackForEdit } | null>(
    null,
  );
  const [scaleOpen, setScaleOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<{ packId: string; name: string } | null>(null);
  const [appending, setAppending] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState("");

  const invalidate = () => {
    utils.storePacks.list.invalidate();
    utils.storePacks.listScales.invalidate();
  };

  const appendLabel = trpc.storePacks.appendLabel.useMutation({
    onSuccess: () => {
      invalidate();
      setAppending(null);
      setLabelDraft("");
      toast.success("Size added");
    },
    onError: (e) => toast.error("Couldn't add the size", { description: storeErrorMessage(e) }),
  });
  const archive = trpc.storePacks.archive.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Pack archived");
    },
    onError: (e) => toast.error("Couldn't archive", { description: storeErrorMessage(e) }),
  });
  const restore = trpc.storePacks.restore.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Pack restored");
    },
    onError: (e) => toast.error("Couldn't restore", { description: storeErrorMessage(e) }),
  });

  const scaleRows = scales.data ?? [];
  const packRows = packs.data ?? [];
  const scaleOptions = scaleRows.map((s) => ({ scaleId: s.scaleId, name: s.name, labels: s.labels }));
  const labelsByScale = new Map(scaleRows.map((s) => [s.scaleId, s.labels]));

  /** Composition in the scale's own order — jsonb key order isn't the run order. */
  const orderedComposition = (scaleId: string, composition: Record<string, number>) => {
    const order = labelsByScale.get(scaleId);
    const entries = Object.entries(composition);
    if (!order) return entries;
    return entries.sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
  };

  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Packs" }]}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Packs</h1>
          <p className="mt-1.5 text-sm text-muted">The runs you ship. Every product price is per pack.</p>
        </div>
        {canManage ? (
          <Button intent="primary" size="md" onClick={() => setPackDialog({ mode: "create" })}>
            <Plus size={14} aria-hidden />
            New pack
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button intent="primary" size="md" disabled>
                  <Plus size={14} aria-hidden />
                  New pack
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{NEEDS_MANAGE}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* ── Size scales ─────────────────────────────────────────────── */}
      <Card className="mt-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">Size scales</h2>
            {canManage ? (
              <Button intent="ghost" size="sm" onClick={() => setScaleOpen(true)}>
                <Plus size={14} aria-hidden />
                New scale
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          {scales.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-6 w-48" />
            </div>
          ) : scales.error ? (
            <Alert intent="bad" description="Couldn't load size scales." />
          ) : scaleRows.length === 0 ? (
            <p className="text-sm text-muted">
              No size scales yet. Sizes are whatever you ship — S to XXL, 28 to 36, 0–3 m.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {scaleRows.map((s) => (
                <li key={s.scaleId} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-24 font-medium text-ink">{s.name}</span>
                  <span className="flex flex-wrap gap-1">
                    {s.labels.map((l) => (
                      <MetaPill key={l}>{l}</MetaPill>
                    ))}
                  </span>
                  {canManage ? (
                    appending === s.scaleId ? (
                      <span className="inline-flex items-center gap-1">
                        <Input
                          value={labelDraft}
                          onChange={(e) => setLabelDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && labelDraft.trim()) {
                              appendLabel.mutate({ scaleId: s.scaleId, label: labelDraft.trim() });
                            }
                            if (e.key === "Escape") {
                              setAppending(null);
                              setLabelDraft("");
                            }
                          }}
                          placeholder="New size"
                          maxLength={8}
                          aria-label={`Add size to ${s.name}`}
                          autoFocus
                          className="h-7 w-24"
                        />
                        <Button
                          intent="ghost"
                          size="sm"
                          disabled={!labelDraft.trim() || appendLabel.isPending}
                          onClick={() =>
                            appendLabel.mutate({ scaleId: s.scaleId, label: labelDraft.trim() })
                          }
                        >
                          <Check size={14} aria-hidden />
                        </Button>
                        <Button
                          intent="ghost"
                          size="sm"
                          onClick={() => {
                            setAppending(null);
                            setLabelDraft("");
                          }}
                        >
                          <X size={14} aria-hidden />
                        </Button>
                      </span>
                    ) : (
                      <Button
                        intent="ghost"
                        size="sm"
                        onClick={() => {
                          setAppending(s.scaleId);
                          setLabelDraft("");
                        }}
                      >
                        <Plus size={14} aria-hidden />
                        Add size
                      </Button>
                    )
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* ── Packs ───────────────────────────────────────────────────── */}
      <Card className="mt-4">
        <CardHeader>
          <h2 className="text-sm font-semibold text-ink">Packs</h2>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Pack</th>
                  <th className="px-4 py-3 font-medium">Scale</th>
                  <th className="px-4 py-3 font-medium">Composition</th>
                  <th className="px-4 py-3 text-right font-medium">Pieces</th>
                  <th className="px-4 py-3 text-right font-medium">Used by</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-center font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {packs.isLoading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b border-line">
                        <td className="px-4 py-3" colSpan={7}>
                          <Skeleton className="h-6 w-full" />
                        </td>
                      </tr>
                    ))
                  : packRows.map((p) => (
                      <tr key={p.packId} className="border-b border-line last:border-0 hover:bg-raised">
                        <td className="px-4 py-3 font-medium text-ink">{p.name}</td>
                        <td className="px-4 py-3 text-muted">{p.scaleName}</td>
                        <td className="px-4 py-3">
                          <span className="flex flex-wrap gap-1">
                            {p.scaleName && Object.keys(p.composition).length > 0
                              ? orderedComposition(p.scaleId, p.composition).map(([label, n]) => (
                                  <MetaPill key={label}>
                                    {label}×{n}
                                  </MetaPill>
                                ))
                              : "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-2">{p.pieces}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-2">
                          {p.productsUsing > 0 ? p.productsUsing : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill tone={p.status === "active" ? "ok" : "muted"} size="sm" dot>
                            {p.status}
                          </StatusPill>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            {canManage ? (
                              <DropdownMenuRoot>
                                <DropdownMenuTrigger asChild>
                                  <Button intent="ghost" size="sm" aria-label={`Actions for ${p.name}`}>
                                    <MoreHorizontal size={16} aria-hidden />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {/* R3: referenced packs are versioned, never edited in place. */}
                                  {p.referenced ? (
                                    <DropdownMenuItem
                                      onSelect={() =>
                                        setPackDialog({
                                          mode: "version",
                                          pack: {
                                            packId: p.packId,
                                            name: p.name,
                                            scaleId: p.scaleId,
                                            composition: p.composition,
                                          },
                                        })
                                      }
                                    >
                                      New version
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      onSelect={() =>
                                        setPackDialog({
                                          mode: "edit",
                                          pack: {
                                            packId: p.packId,
                                            name: p.name,
                                            scaleId: p.scaleId,
                                            composition: p.composition,
                                          },
                                        })
                                      }
                                    >
                                      Edit
                                    </DropdownMenuItem>
                                  )}
                                  {p.status === "active" ? (
                                    <DropdownMenuItem
                                      onSelect={() => setArchiveTarget({ packId: p.packId, name: p.name })}
                                    >
                                      Archive
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onSelect={() => restore.mutate({ packId: p.packId })}>
                                      Restore
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenuRoot>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                {!packs.isLoading && packRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted">
                      No packs yet. Define the runs you ship — every product price is per pack.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {packDialog ? (
        <PackDialog
          open
          onOpenChange={(o) => !o && setPackDialog(null)}
          mode={packDialog.mode}
          scales={scaleOptions}
          pack={packDialog.pack}
          referencedNote={
            packDialog.mode === "version"
              ? "Products and orders reference the current version — they keep it. This creates a new one."
              : undefined
          }
          onSaved={invalidate}
          onNeedScale={() => {
            setPackDialog(null);
            setScaleOpen(true);
          }}
        />
      ) : null}

      <ScaleDialog open={scaleOpen} onOpenChange={setScaleOpen} onSaved={invalidate} />

      <ConfirmDialog
        open={archiveTarget !== null}
        onOpenChange={(o) => !o && setArchiveTarget(null)}
        title="Archive this pack?"
        description="Products already priced with it keep it; it just leaves the picker."
        confirmLabel="Archive"
        pending={archive.isPending}
        onConfirm={async () => {
          if (archiveTarget) await archive.mutateAsync({ packId: archiveTarget.packId });
          setArchiveTarget(null);
        }}
      />
    </StoreAdminLayout>
  );
}
